import { AccountSelector } from '@/design-system/components/accountSelector/AccountSelector';
import { Dropdown } from '@/design-system/components/dropdown';
import { UniInput } from '@/design-system/components/inputs';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { Pressable, SxProp, View } from 'dripsy';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UseBillPaymentFormProp } from '../../hooks/useBillPaymentForm.hook';
import { toNumber } from '../../utils/utils';

export interface DetailFormComponentWebProps {
  sx?: SxProp;
  billPaymentProp: UseBillPaymentFormProp;
}

export const DetailFormWebComponent: React.FC<DetailFormComponentWebProps> = ({ sx, billPaymentProp }) => {
  const { t } = useTranslation('global-bill-payment');
  const {
    detailForm: {
      bank,
      bankAccountNumber,
      additionalDetail,
      assetName,
      amount,
      assetList,
      walletId,
      setBankName,
      setBankAccountNumber,
      setDebitAccountWallerId,
      setDebitAccountWalletName,
      setAdditionalDetail,
      setAssetName,
      setAmount,
      setWalletId
    },
    scheduleForm: { remarks, setRemarks, onValidateScheduleForm },
    general: { billPaymentState }
  } = billPaymentProp;

  const onAssetChange = (value: string) => {
    setAssetName(value);
    setDebitAccountWallerId(undefined);
    setDebitAccountWalletName(undefined);
  };

  const renderErrorAmountLayout = () => {
    if (billPaymentState.amount?.error) {
      const number = toNumber(amount);
      if (number <= 0) {
        return (
          <>
            <TextCaption danger sx={{ mt: -12 }}>
              {t('global-bill-payment::instruction.text--greater-than-zero')}
            </TextCaption>
          </>
        );
      } else {
        return (
          <>
            <TextCaption danger sx={{ mt: -12 }}>
              {t('global-bill-payment::payment-details.text--amount- insufficient')}
              {t('global-bill-payment::payment-details.text--inject-funds-3')}
              {t('global-bill-payment::payment-details.text--inject-funds-1')}{' '}
              <Pressable onPress={() => iframePostMessage('showInjectionInstruction', true)}>
                <TextCaption danger semiBold={true} sx={sxStyles.textUnderline}>
                  {t('global-bill-payment::instruction.button--injection')}
                </TextCaption>
              </Pressable>{' '}
              {t('global-bill-payment::payment-details.text--inject-funds-2')}{' '}
              <Pressable onPress={() => iframePostMessage('moveToExchange', true)}>
                <TextCaption danger semiBold={true} sx={sxStyles.textUnderline}>
                  {t('global-bill-payment::instruction.button--exchange')}
                </TextCaption>
              </Pressable>
              .
            </TextCaption>
          </>
        );
      }
    } else return <></>;
  };

  return (
    <View sx={{ ...sxStyles.container, ...sx }}>
      <View sx={sxStyles.leftComponent}>
        <View sx={sxStyles.number}>
          <TextBody sx={sxStyles.textNumber}>2</TextBody>
        </View>
        <TextCaption semiBold={true}>{t('global-bill-payment::instruction.text--enter-details')}</TextCaption>
        <TextCaption sx={sxStyles.marginTop8}>{t('global-bill-payment::instruction.text--required-5-business')}</TextCaption>
      </View>
      <View sx={sxStyles.flex4} />
      <View sx={sxStyles.rightComponent}>
        <View sx={sxStyles.viewInputWrapper}>
          <UniInput
            value={bank}
            labelText={t('global-bill-payment::glossary.merchant-bank-name')}
            placeholder="Super Merchant Ltd."
            errorText={billPaymentState.bank ? billPaymentState.bank.message : undefined}
            onValueChange={setBankName}
            wrapperSx={sxStyles.inputWrapper}
            sx={sxStyles.inputFullWidth}
          />
        </View>
        <View sx={sxStyles.viewInputWrapper}>
          <UniInput
            wrapperSx={sxStyles.inputWrapper}
            sx={sxStyles.inputFullWidth}
            value={bankAccountNumber}
            labelText={t('global-bill-payment::glossary.bill-bank-account-number')}
            placeholder="ABC1234567890"
            onValueChange={setBankAccountNumber}
            errorText={billPaymentState.bankAccountNumber ? billPaymentState.bankAccountNumber.message : undefined}
          />
        </View>
        <View sx={sxStyles.viewInputWrapper}>
          <UniInput
            value={additionalDetail}
            labelText={t('global-bill-payment::glossary.payment-additional-details-optional')}
            placeholder={t('global-bill-payment::glossary.please-enter-details')}
            onValueChange={setAdditionalDetail}
            textAlignVertical="top"
            multiline
            sx={{ ...sxStyles.viewInputOptional, ...sxStyles.inputFullWidth }}
            inputSx={sxStyles.inputOptional}
            wrapperSx={sxStyles.inputWrapper}
          />
        </View>
        <View sx={sxStyles.mobileLayout}>
          <View sx={sxStyles.viewDropdownWrapper}>
            <Dropdown
              sx={{
                ...sxStyles.mobileCode,
                ...{ borderColor: billPaymentState.amount && billPaymentState.amount.error ? '$danger' : '$borderButton' }
              }}
              onValueChange={onAssetChange}
              options={assetList}
              value={assetName}
              label={t('global-bill-payment::glossary.bill-amount')}
              placeholder="Asset"
            />
          </View>

          <View sx={sxStyles.flex1}>
            <UniInput
              value={amount}
              labelText=" "
              placeholder="0"
              defaultValue={amount}
              errorText={billPaymentState.amount?.error ? ' ' : undefined}
              wrapperSx={sxStyles.inputWrapper}
              sx={sxStyles.inputFullWidth}
              keyboardType={'decimal-pad'}
              onValueChange={(value) => {
                setAmount(formatAmountByDecimals({ value: toNumber(value) }));
              }}
            />
          </View>
        </View>
        {renderErrorAmountLayout()}
        <View sx={sxStyles.viewDebitAccount}>
          <TextCaption semiBold sx={sxStyles.textDebitAccount}>
            {t('global-bill-payment::glossary.debit-account')}
          </TextCaption>
          <AccountSelector
            asset={assetName}
            type={'currency'}
            onValueChange={setWalletId}
            value={walletId}
            onWalletChange={(value) => {
              if (value) {
                setDebitAccountWallerId(value.accountNumber);
                setDebitAccountWalletName(value.label);

                billPaymentState.wallet?.error && onValidateScheduleForm();
              }
            }}
            sx={{
              mb: billPaymentState.wallet?.error ? '$1' : '$5'
            }}
            showBalance={true}
          />
          {billPaymentState.wallet?.error && (
            <TextCaption
              danger
              sx={{
                mb: '$5'
              }}>
              {billPaymentState.wallet?.message}
            </TextCaption>
          )}
        </View>

        <View sx={{ mt: '$5' }}>
          <UniInput
            labelText={t('global-bill-payment::general.remarks-option')}
            sx={sxStyles.viewRemarksWrapper}
            inputSx={sxStyles.inputRemarks}
            wrapperSx={sxStyles.inputWrapperRemark}
            textAlignVertical="top"
            multiline
            onValueChange={setRemarks}
            value={remarks}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: 'transparent',
    flexDirection: 'row',
    width: '100%'
  },
  leftComponent: {
    flex: 29,
    py: '$1',
    pl: '$8',
    flexDirection: 'column'
  },
  rightComponent: {
    flex: 52,
    flexDirection: 'column',
    mt: '$1'
  },
  number: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '$6',
    height: '$6',
    borderRadius: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '$secondary'
  },
  textNumber: {
    color: '$white'
  },
  mobileLayout: {
    width: '100%',
    flexDirection: 'row',
    zIndex: 1000
  },
  mobileCode: {
    width: 100,
    height: '$11',
    mr: 8
  },
  viewDebitAccount: {
    zIndex: 500,
    mt: '$5',
    mb: -20
  },
  textDebitAccount: {
    mb: '$1'
  },
  viewInputWrapper: {
    mb: '$5'
  },
  viewDropdownWrapper: {
    flexDirection: 'column',
    zIndex: 1000
  },
  viewInputOptional: {
    height: '$17'
  },
  inputOptional: {
    height: '100%',
    pt: '$4'
  },
  inputFullWidth: {
    width: '100%'
  },
  inputWrapper: {
    alignItems: 'flex-start'
  },
  flex4: {
    flex: 4
  },
  marginTop4: {
    mt: '$1'
  },
  marginTop8: {
    mt: '$2'
  },
  flex1: {
    flex: 1
  },
  viewRemarksWrapper: {
    width: '100%',
    height: '$17'
  },
  inputRemarks: {
    height: '100%',
    pt: '$4'
  },
  inputWrapperRemark: {
    alignItems: 'flex-start'
  },
  textUnderline: {
    textDecorationLine: 'underline'
  }
};
